import { useRef, useState, type ReactNode } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cx } from "@/lib/cx";
import { popperMaterial } from "./Menu";
import { LAYER } from "./surface";
import { useBackClose } from "./useBackClose";
import { useCloseOnlyOutside } from "./useCloseOnlyOutside";

/**
 * A small, dismissible surface attached to a control.
 *
 * The app had none, so anything that needed one grew into the page instead —
 * the clearest case being the day navigator, which was two controls and a raw
 * `<input type="date">` where it should be one button that opens a calendar
 * (UI-REVIEW P14). {@link DatePicker} is this component with a calendar in it.
 *
 * Unlike {@link Dialog} this is *not* a modal by default: the page behind it
 * keeps scrolling, and a click outside closes it — and only closes it
 * ({@link useCloseOnlyOutside}). Pass `modal` only when the
 * content must be completed before anything else happens, which is rare.
 *
 * It shares {@link popperMaterial} with {@link Menu} and {@link Select}, so all
 * three use the same opaque raised surface and 8px continuous corner. The
 * shared opacity rule keeps dense content from showing through on both desktop
 * and Android WebView. Three poppers with three surface recipes is the drift
 * this constant exists to stop.
 */

export type PopoverProps = {
  /** Must be a single element that can take a ref — Radix clones it. */
  trigger: ReactNode;
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
  /** Accessible name for the surface. Give one whenever it has no heading. */
  label?: string;
  /** Traps focus and makes the rest of the page inert. Default off. */
  modal?: boolean;
  /** Internal padding. Off for a calendar or a list that owns its own. */
  padded?: boolean;
  className?: string;
};

export function Popover({
  trigger,
  children,
  open,
  onOpenChange,
  defaultOpen,
  side = "bottom",
  align = "start",
  sideOffset = 6,
  label,
  modal = false,
  padded = true,
  className,
}: PopoverProps) {
  // Controllable, because closing on an outside click is done here rather than
  // by Radix, and that needs to know whether the popover is open.
  const [uncontrolled, setUncontrolled] = useState(defaultOpen ?? false);
  const isOpen = open ?? uncontrolled;
  const setOpen = (next: boolean) => {
    if (open === undefined) {
      setUncontrolled(next);
    }
    onOpenChange?.(next);
  };
  const nothing = useRef<Element>(null);
  useCloseOnlyOutside(isOpen && !modal, () => setOpen(false), nothing);
  useBackClose(isOpen, () => setOpen(false));

  return (
    <PopoverPrimitive.Root open={isOpen} onOpenChange={setOpen} modal={modal}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          data-busee-anim="pop"
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          aria-label={label}
          className={cx(popperMaterial, LAYER.overlay, padded && "p-3", className)}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

/** Closes the popover from inside its content, without threading a callback. */
export const PopoverClose = PopoverPrimitive.Close;
